import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../api'
import { FALLBACK_TZ, formatHHmm, todayIso } from '../panel/lib/time'
import { DayStrip } from '../DayStrip'
import {
  bookResource,
  getResource,
  getSlots,
  type ResourceBookingResult,
  type ResourceCard,
  type Slot,
} from './api'

const errorText = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback

/** `/r/<id>`: дата → свободный слот → имя и телефон → запись. */
export function ResourcePage({ resourceId }: { resourceId: number }) {
  const [resource, setResource] = useState<ResourceCard | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [slots, setSlots] = useState<Slot[] | null>(null)
  const [slot, setSlot] = useState<Slot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ResourceBookingResult | null>(null)

  const tz = resource?.location?.timezone ?? FALLBACK_TZ

  useEffect(() => {
    getResource(resourceId)
      .then((r) => {
        setResource(r)
        document.title = `${r.name} — запись`
        setDate(todayIso(r.location?.timezone ?? FALLBACK_TZ))
      })
      .catch(() => setFatal('Ресурс не найден — проверьте ссылку'))
  }, [resourceId])

  useEffect(() => {
    if (!date) return
    // Ответ за прошлую дату может прийти позже — он не должен затереть текущую.
    let stale = false
    setSlots(null)
    setSlot(null)
    setError(null)
    getSlots(resourceId, date)
      .then((s) => !stale && setSlots(s))
      .catch((err: unknown) => {
        if (stale) return
        setSlots([])
        setError(errorText(err, 'Не удалось загрузить расписание'))
      })
    return () => {
      stale = true
    }
  }, [resourceId, date, result])

  if (fatal || (resource && !resource.isAvailable)) {
    return (
      <main className="card card--wide">
        <h1>Не получилось</h1>
        <p className="muted">{fatal ?? 'Запись на этот ресурс сейчас закрыта'}</p>
      </main>
    )
  }

  if (!resource) {
    return (
      <main className="card card--wide">
        <p className="muted">Загружаем…</p>
      </main>
    )
  }

  const when = (s: { startTime: string; endTime: string }) =>
    `${new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: tz }).format(
      new Date(s.startTime),
    )}, ${formatHHmm(new Date(s.startTime), tz)}–${formatHHmm(new Date(s.endTime), tz)}`

  if (result) {
    return (
      <main className="card card--wide">
        <h1>Вы записаны</h1>
        <p className="done">
          {resource.name}, {when(result)}
        </p>
        <button type="button" onClick={() => setResult(null)}>
          Записаться ещё
        </button>
      </main>
    )
  }

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!slot) return
    const form = new FormData(e.currentTarget)
    setBusy(true)
    setError(null)
    try {
      setResult(
        await bookResource({
          roomId: resource.id,
          startTime: slot.startTime,
          endTime: slot.endTime,
          clientName: String(form.get('name') ?? '').trim(),
          clientPhone: String(form.get('phone') ?? '').trim(),
        }),
      )
    } catch (err: unknown) {
      setError(errorText(err, 'Не удалось записаться'))
    } finally {
      setBusy(false)
    }
  }

  // Сетка на сегодня включает прошедшие слоты — их не предлагаем.
  const now = Date.now()
  const free = (slots ?? []).filter((s) => s.available && new Date(s.startTime).getTime() > now)

  return (
    <main className="card card--wide">
      <header className="hero">
        {resource.location && <p className="eyebrow">{resource.location.name}</p>}
        <h1>{resource.name}</h1>
        {resource.description && <p className="lead">{resource.description}</p>}
        {resource.pricePerHour > 0 && (
          <p className="price">{resource.pricePerHour.toLocaleString('ru-RU')} ₽/ч</p>
        )}
      </header>

      <form className="panel" onSubmit={submit}>
        <DayStrip today={todayIso(tz)} value={date} onChange={setDate} />

        <section className="section">
          <div className="section__head">
            <h2>Выберите время</h2>
          </div>
          <div className="times" role="group" aria-label="Свободное время">
            {slots === null ? (
              <p className="muted">Загружаем…</p>
            ) : free.length === 0 ? (
              <p className="muted">На этот день свободного времени нет</p>
            ) : (
              free.map((s) => (
                <button
                  key={s.startTime}
                  type="button"
                  className="chip"
                  aria-pressed={slot?.startTime === s.startTime}
                  onClick={() => setSlot(s)}
                >
                  {formatHHmm(new Date(s.startTime), tz)}
                  {s.seatsTotal > 1 && <small>{s.seatsFree} мест</small>}
                </button>
              ))
            )}
          </div>
        </section>

        <section className="section">
          <div className="section__head">
            <h2>Ваши данные</h2>
          </div>
          <div className="fields">
            <input name="name" required autoComplete="name" placeholder="Имя *" aria-label="Имя" />
            <input
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              placeholder="Телефон * — +7 999 111-22-33"
              aria-label="Телефон"
            />
          </div>
        </section>

        {slot && <p className="summary">{when(slot)}</p>}
        {error && <p className="error">{error}</p>}

        <button type="submit" className="cta" disabled={busy || !slot}>
          {busy ? 'Записываем…' : slot ? 'Записаться →' : 'Выберите время'}
        </button>
      </form>
    </main>
  )
}
